import {
  CACHE_FAILURE_MESSAGES,
  readCacheFailureReason,
  type CacheFailureReason,
  type PwaEnvironment,
  type PwaRegistrationHandle,
  type PwaStatusSnapshot,
  type RegisterPwaServiceWorker
} from "./types";

interface PwaControllerDependencies {
  environment: PwaEnvironment;
  registerServiceWorker: RegisterPwaServiceWorker;
}

interface PwaInternalState {
  isOnline: boolean;
  isOfflineReady: boolean;
  isUpdateAvailable: boolean;
  isUpdateSafe: boolean;
  isApplyingUpdate: boolean;
  cacheFailureMessage: string | null;
}

function createSnapshot(state: PwaInternalState): PwaStatusSnapshot {
  return {
    isOnline: state.isOnline,
    isOfflineReady: state.isOfflineReady,
    isUpdateAvailable: state.isUpdateAvailable,
    isUpdateSafe: state.isUpdateSafe,
    isApplyingUpdate: state.isApplyingUpdate,
    cacheFailureMessage: state.cacheFailureMessage
  };
}

function statesMatch(left: PwaInternalState, right: PwaInternalState): boolean {
  return (
    left.isOnline === right.isOnline &&
    left.isOfflineReady === right.isOfflineReady &&
    left.isUpdateAvailable === right.isUpdateAvailable &&
    left.isUpdateSafe === right.isUpdateSafe &&
    left.isApplyingUpdate === right.isApplyingUpdate &&
    left.cacheFailureMessage === right.cacheFailureMessage
  );
}

export class PwaController {
  private readonly environment: PwaEnvironment;
  private readonly registerServiceWorker: RegisterPwaServiceWorker;
  private readonly listeners = new Set<() => void>();
  private state: PwaInternalState;
  private snapshot: PwaStatusSnapshot;
  private registrationStarted = false;
  private registrationAttempt = 0;
  private registration: PwaRegistrationHandle | null = null;
  private disconnectConnectivity: (() => void) | null = null;
  private disconnectVisibility: (() => void) | null = null;
  private disconnectServiceWorkerMessages: (() => void) | null = null;

  constructor({ environment, registerServiceWorker }: PwaControllerDependencies) {
    this.environment = environment;
    this.registerServiceWorker = registerServiceWorker;
    this.state = {
      isOnline: environment.isOnline(),
      isOfflineReady: false,
      isUpdateAvailable: false,
      isUpdateSafe: false,
      isApplyingUpdate: false,
      cacheFailureMessage: null
    };
    this.snapshot = createSnapshot(this.state);
  }

  readonly getSnapshot = (): PwaStatusSnapshot => this.snapshot;

  readonly subscribe = (listener: () => void): (() => void) => {
    const subscription = () => listener();
    const shouldConnect = this.listeners.size === 0;

    this.listeners.add(subscription);

    if (shouldConnect) {
      this.connect();
    }

    let isSubscribed = true;

    return () => {
      if (!isSubscribed) {
        return;
      }

      isSubscribed = false;
      this.listeners.delete(subscription);

      if (this.listeners.size === 0) {
        this.disconnect();
      }
    };
  };

  setUpdateSafe(isUpdateSafe: boolean): void {
    this.updateState({ isUpdateSafe });

    if (isUpdateSafe) {
      void this.applyUpdate();
    }
  }

  dismissCacheFailure(): void {
    this.updateState({ cacheFailureMessage: null });
  }

  reportCacheFailure(reason: CacheFailureReason = "unknown"): void {
    this.updateState({
      isOfflineReady: false,
      cacheFailureMessage: CACHE_FAILURE_MESSAGES[reason]
    });
  }

  async applyUpdate(): Promise<boolean> {
    if (
      !this.state.isUpdateAvailable ||
      !this.state.isUpdateSafe ||
      this.state.isApplyingUpdate ||
      this.registration === null
    ) {
      return false;
    }

    this.updateState({ isApplyingUpdate: true });

    try {
      await this.registration.applyUpdate();
      this.environment.reloadPage();
      this.updateState({
        isApplyingUpdate: false,
        isUpdateAvailable: false
      });
      return true;
    } catch {
      this.updateState({ isApplyingUpdate: false });
      this.reportCacheFailure("registration");
      return false;
    }
  }

  private connect(): void {
    this.updateState({ isOnline: this.environment.isOnline() });
    this.disconnectConnectivity = this.environment.subscribeToConnectivity(
      () => {
        this.updateState({ isOnline: true });
        this.startRegistration();
        void this.checkForUpdate();
      },
      () => this.updateState({ isOnline: false })
    );
    this.disconnectVisibility = this.environment.subscribeToVisibility(() => {
      void this.checkForUpdate();
    });
    this.disconnectServiceWorkerMessages =
      this.environment.subscribeToServiceWorkerMessages((data) => {
        const reason = readCacheFailureReason(data);

        if (reason !== null) {
          this.reportCacheFailure(reason);
        }
      });

    this.startRegistration();
    void this.checkForUpdate();
  }

  private disconnect(): void {
    this.disconnectConnectivity?.();
    this.disconnectVisibility?.();
    this.disconnectServiceWorkerMessages?.();
    this.disconnectConnectivity = null;
    this.disconnectVisibility = null;
    this.disconnectServiceWorkerMessages = null;
  }

  private startRegistration(): void {
    if (this.registrationStarted) {
      return;
    }

    this.registrationStarted = true;
    const registrationAttempt = ++this.registrationAttempt;

    try {
      const registration = this.registerServiceWorker({
        onNeedRefresh: () => {
          if (!this.isRegistrationAttemptActive(registrationAttempt)) {
            return;
          }

          this.updateState({ isUpdateAvailable: true });
          void this.applyUpdate();
        },
        onOfflineReady: () => {
          if (!this.isRegistrationAttemptActive(registrationAttempt)) {
            return;
          }

          this.updateState({
            isOfflineReady: true,
            cacheFailureMessage: null
          });
        },
        onRegisterError: () =>
          this.handleRegistrationFailure(registrationAttempt)
      });

      if (this.isRegistrationAttemptActive(registrationAttempt)) {
        this.registration = registration;
        void this.applyUpdate();
      }
    } catch {
      this.handleRegistrationFailure(registrationAttempt);
    }
  }

  private handleRegistrationFailure(registrationAttempt: number): void {
    if (!this.isRegistrationAttemptActive(registrationAttempt)) {
      return;
    }

    this.registrationStarted = false;
    this.registration = null;
    this.reportCacheFailure("registration");
  }

  private async checkForUpdate(): Promise<void> {
    if (!this.state.isOnline || this.registration === null) {
      return;
    }

    try {
      await this.registration.checkForUpdate();
    } catch {
      // A transient freshness check must not invalidate already cached content.
    }
  }

  private isRegistrationAttemptActive(registrationAttempt: number): boolean {
    return (
      this.registrationStarted &&
      this.registrationAttempt === registrationAttempt
    );
  }

  private updateState(patch: Partial<PwaInternalState>): void {
    const nextState = { ...this.state, ...patch };

    if (statesMatch(this.state, nextState)) {
      return;
    }

    this.state = nextState;
    this.snapshot = createSnapshot(nextState);

    for (const listener of this.listeners) {
      listener();
    }
  }
}
